// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { zodResolver } from '@hookform/resolvers/zod';
import { type FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useAccountWorkflowData } from '@shared/advisory/account-workflow/providers';
import {
  signatureSchema,
  type SignatureFormData,
} from '@shared/advisory/account-workflow/types';
import { useSnackbar, useStepsData } from '@shared/ui-kit';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

export const useSignature = () => {
  const {
    workflow,
    markStepAsCompleted,
    isStepCompleted,
    getFormData,
    saveFormData,
    isUpdatingClientOnboardingProfile,
    saveAccountOpeningData,
    switchToProfilingBlocks,
    setNavigationFloor,
  } = useAccountWorkflowData();

  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { next, navigate } = useStepsData();

  const savedData = getFormData('signature');

  const form = useForm<SignatureFormData>({
    resolver: zodResolver(signatureSchema),
    mode: 'onChange',
    defaultValues: savedData,
  });

  const isFormValid = useCallback(() => {
    return form.formState.isValid;
  }, [form.formState.isValid]);

  const onNext = useCallback(
    (data: SignatureFormData) => {
      const advance = () => {
        markStepAsCompleted('signature');

        if (workflow === 'combined') {
          switchToProfilingBlocks();
          setNavigationFloor('contract-review');
          navigate('contract-review');
          return;
        }

        next();
      };

      if (isStepCompleted('signature', form.formState.isDirty)) {
        advance();
        return;
      }

      saveFormData('signature', data);

      saveAccountOpeningData({ submitToInversis: true })
        .then(() => {
          advance();
        })
        .catch((error: FetchBaseQueryError) => {
          const errorData = error?.data as { error_code?: string } | undefined;

          if (errorData?.error_code === 'VALIDATION_ERROR') {
            enqueueSnackbar(
              t(
                'account-opening.signature.submit-validation-biometric-incomplete',
                {
                  ns: 'components',
                },
              ),
              {
                variant: 'error',
                onClose: () => {
                  navigate('identity-verification');
                },
              },
            );

            return;
          }

          enqueueSnackbar(
            t('account-opening.signature.submit-error', { ns: 'components' }),
            {
              variant: 'error',
            },
          );
        });
    },
    [
      isStepCompleted,
      form.formState.isDirty,
      saveFormData,
      saveAccountOpeningData,
      markStepAsCompleted,
      workflow,
      next,
      switchToProfilingBlocks,
      setNavigationFloor,
      navigate,
      enqueueSnackbar,
      t,
    ],
  );

  return {
    form,
    onNext,
    isFormValid,
    isUpdatingClientOnboardingProfile,
  };
};
